import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
  viewChildren
} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, UntypedFormControl} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {TranslateModule, TranslateService} from '@ngx-translate/core';

import {BaseModel} from '../../core/definitions/base-model';
import {CmsApiService} from '../../core/cms-api.service';
import {FieldKeyService} from '../../core/field-key.service';
import {FieldParameters} from '../../core/definitions/field-parameters';
import {FieldStateService} from '../../core/field-state.service';
import {FieldValueService} from '../../core/field-value.service';
import {GetTemplateFieldsParams, TemplateField, TemplateFieldSelect} from '../../core/definitions/template-field';
import {InFocus} from '../in-focus';
import {LoggerService} from '../../core/logger.service';
import {UiToolsService} from '../../core/ui-tools.service';
import {
  buildTemplateFieldRows,
  conditionSourceValue,
  TemplateFieldRow,
  TemplateFieldRowObject,
  usedTemplateFieldKeys
} from './template-field-rows';

@Component({
  selector: 'app-edit-field-select-template-field',
  templateUrl: './edit-field-select-template-field.component.html',
  styleUrl: './edit-field-select-template-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatIconModule, MatProgressSpinnerModule, TranslateModule],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => EditFieldSelectTemplateFieldComponent)
  }]
})
export class EditFieldSelectTemplateFieldComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private readonly cmsApi = inject(CmsApiService);
  private readonly fieldKeyService = inject(FieldKeyService);
  private readonly fieldState = inject(FieldStateService);
  private readonly fieldValue = inject(FieldValueService);
  private readonly logger = inject(LoggerService);
  private readonly translate = inject(TranslateService);
  private readonly uiTools = inject(UiToolsService);

  readonly fieldParameters = input.required<FieldParameters>();
  readonly inFocus = input<InFocus>();

  readonly fieldFocus = output<void>();
  readonly fieldBlur = output<void>();

  protected readonly activeIndex = signal(0);
  protected readonly loading = signal(false);
  protected readonly query = signal('');
  protected readonly showOptions = signal(false);
  protected readonly rows = computed(() => buildTemplateFieldRows(
    this.fields(), this.query(), this.expandedGroupIds(), this.usedKeys()));
  protected readonly activeRowId = computed(() =>
    this.showOptions() ? this.rows()[this.activeIndex()]?.id ?? null : null);

  protected fieldKey = '';
  protected searchPlaceholder = '';

  private readonly fields = signal<TemplateField[]>([]);
  private readonly expandedGroupIds = signal(new Set<string>());
  private readonly usedKeys = signal(new Set<string>());
  private readonly rowItems = viewChildren<ElementRef<HTMLElement>>('rowItem');
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  private clickListenerId: string;
  private loadedParamsKey: string;
  ngOnInit(): void {
    const fieldParameters = this.fieldParameters();
    const field = fieldParameters.field;
    this.fieldKey = this.fieldKeyService.getFieldKey(
      field, fieldParameters.parentIndex, fieldParameters.index, '', fieldParameters.grandparentIndex);
    this.searchPlaceholder = '\ue62c\u00a0'
      + this.translate.instant('TRANS__FIELD_SELECT__SEARCH_PLACEHOLDER')
      + this.fieldState.getFieldTitle(field, fieldParameters.rootObject, true, true);
    this.query.set(this.fieldValue.getValueCompanion(fieldParameters.object, field.name) || '');
  }

  ngOnDestroy(): void {
    this.uiTools.removeDocumentClickListener(this.clickListenerId);
  }

  protected get listId(): string {
    return `${this.fieldKey}-options`;
  }

  protected get ignoreClicksClass(): string {
    const fieldName = this.fieldParameters().field.name;
    const sectionsContainer = this.fieldParameters().sectionsContainer;
    if (sectionsContainer.isSecondDialog) {
      return `ignoreClicksSecondModal-${fieldName}`;
    }
    return sectionsContainer.isDialog ? `ignoreClicksModal-${fieldName}` : `ignoreClicks-${fieldName}`;
  }

  protected openOptions(): void {
    this.toggleShowOptions(true);
    this.searchInput().nativeElement.focus();
  }

  protected toggleOptions(): void {
    if (this.showOptions()) {
      this.toggleShowOptions(false);
    } else {
      this.openOptions();
    }
  }

  protected onQueryChange(value: string): void {
    this.query.set(value);
    this.activeIndex.set(0);
    this.toggleShowOptions(true);
  }

  protected onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(event.key === 'ArrowDown' ? 1 : -1);
        break;
      case 'ArrowRight':
      case 'ArrowLeft':
        this.onHorizontalKey(event, event.key === 'ArrowRight');
        break;
      case 'Enter':
        event.preventDefault();
        this.activateRow(this.rows()[this.activeIndex()]);
        break;
      case 'Escape':
      case 'Tab':
        this.toggleShowOptions(false);
        break;
    }
  }

  protected activateRow(row: TemplateFieldRow): void {
    if (!row) {
      return;
    }
    if (row.isGroup) {
      this.setGroupExpanded(row.field, !row.expanded);
    } else {
      this.selectField(row.field);
    }
  }

  protected clearField(): void {
    this.writeSelection(null, '', null);
    if (!this.selectConfig.condition_mode) {
      this.fieldParameters().object['field_uuid'] = null;
      this.fieldParameters().object['parent_ids'] = [];
    }
    this.query.set('');
    this.setControlValue('');
  }

  private selectField(selected: TemplateField): void {
    const object = this.fieldParameters().object;
    if (this.selectConfig.condition_mode) {
      this.writeSelection(selected.artifact_id, selected.field_title, conditionSourceValue(
        selected.superobject_type_id, this.columnRow['source_superobject_type_id'] ?? null, this.modelTypeId));
    } else {
      this.writeSelection(selected.artifact_id, selected.field_title, selected.superobject_type_id);
      object['field_uuid'] = selected.field_uuid;
      object['parent_ids'] = selected.parent_field_ids;
    }
    this.query.set(selected.field_title);
    this.setControlValue(selected.field_title);
    this.toggleShowOptions(false);
  }

  private setControlValue(value: string): void {
    const control = this.formControl;
    control?.setValue(value);
    control?.markAsDirty();
  }

  private writeSelection(value: string | null, title: string, sourceId: string | null): void {
    const object = this.fieldParameters().object;
    const fieldName = this.fieldParameters().field.name;
    object[fieldName] = value;
    object[`${fieldName}_value`] = title;
    object[this.selectConfig.source_field] = sourceId;
  }

  private onHorizontalKey(event: KeyboardEvent, expand: boolean): void {
    const row = this.rows()[this.activeIndex()];
    if (!this.showOptions() || !row) {
      return;
    }
    event.preventDefault();
    if (row.isGroup) {
      this.setGroupExpanded(row.field, expand);
    } else if (!expand && row.parentGroupId) {
      this.activeIndex.set(this.rows().findIndex(candidate => candidate.id === row.parentGroupId));
    }
  }

  private setGroupExpanded(group: TemplateField, expanded: boolean): void {
    const ids = new Set(this.expandedGroupIds());
    const groupId = group.superobject_type_id || group.artifact_id;
    if (expanded) {
      ids.add(groupId);
    } else {
      ids.delete(groupId);
    }
    this.expandedGroupIds.set(ids);
    this.activeIndex.set(this.rows().findIndex(row => row.isGroup && row.field === group));
  }

  private moveActive(step: number): void {
    if (!this.showOptions()) {
      this.openOptions();
      return;
    }
    const count = this.rows().length;
    if (!count) {
      return;
    }
    this.activeIndex.update(index => (index + step + count) % count);
    this.rowItems()[this.activeIndex()]?.nativeElement.scrollIntoView({block: 'nearest'});
  }

  private toggleShowOptions(show: boolean): void {
    if (show === this.showOptions()) {
      return;
    }
    this.showOptions.set(show);
    if (show) {
      this.usedKeys.set(this.selectConfig.condition_mode ? new Set() : usedTemplateFieldKeys(
        this.siblingRows(), this.fieldParameters().object));
      this.clickListenerId = this.uiTools.addDocumentClickListener(
        (event: Event) => this.checkOutsideClick(event), this.ignoreClicksClass);
      this.loadFields().then();
    } else {
      this.uiTools.removeDocumentClickListener(this.clickListenerId);
      this.fieldBlur.emit();
    }
  }

  private async loadFields(): Promise<void> {
    const params = this.requestParams();
    const paramsKey = JSON.stringify(params);
    if (paramsKey === this.loadedParamsKey) {
      return;
    }
    this.loadedParamsKey = paramsKey;
    this.loading.set(true);
    try {
      const result = await this.cmsApi.getTemplateFields(params);
      if (paramsKey === this.loadedParamsKey) {
        this.fields.set(result.fields);
        this.activeIndex.set(0);
      }
    } catch (error) {
      this.loadedParamsKey = undefined;
      this.logger.error(`Could not get template fields: ${error}`);
    } finally {
      this.loading.set(false);
    }
  }

  private requestParams(): GetTemplateFieldsParams {
    const params: GetTemplateFieldsParams = {model_type_id: this.modelTypeId};
    if (!this.selectConfig.condition_mode) {
      if (this.selectConfig.index_query_field_required) {
        params.index_query_field_required = true;
      }
      return params;
    }
    params.condition_mode = true;
    const columnSource = this.columnRow['source_superobject_type_id'];
    const columnParentIds: string[] = this.columnRow['parent_ids'] ?? [];
    if (columnSource) {
      params.column_source_superobject_type_id = columnSource;
    }
    if (columnParentIds.length) {
      params.column_parent_ids = columnParentIds.join(',');
    }
    return params;
  }

  private siblingRows(): TemplateFieldRowObject[] {
    const object = this.fieldParameters().object;
    const rows = Object.values(this.fieldParameters().rootObject).find(value => Array.isArray(value) && value.includes(object));
    return (rows as TemplateFieldRowObject[]) ?? [];
  }

  private get selectConfig(): TemplateFieldSelect {
    return this.fieldParameters().field.template_field_select;
  }

  private get modelTypeId(): string {
    return this.fieldParameters().rootObject['model_type_id'];
  }

  private get columnRow(): BaseModel {
    return this.fieldParameters().grandParentObject ?? {} as BaseModel;
  }

  private get formControl(): UntypedFormControl {
    const control = this.fieldParameters().sectionsContainer.formGroup.controls[this.fieldKey] as UntypedFormControl;
    if (!control) {
      this.logger.warn(`No form control for key "${this.fieldKey}"`);
    }
    return control;
  }

  private checkOutsideClick(event: Event): void {
    const target = event.target ?? event;
    const fieldName = this.fieldParameters().field.name;
    const classNames = [this.ignoreClicksClass];
    if (this.fieldParameters().sectionsContainer.isDialog) {
      classNames.push(`ignoreClicks-${fieldName}`);
    }
    if (!classNames.some(className => this.uiTools.findClassNameRecursively(target, className))) {
      this.toggleShowOptions(false);
    }
  }

  registerOnChange(): void {
    // No implementation necessary
  }

  registerOnTouched(): void {
    // No implementation necessary
  }

  setDisabledState(): void {
    // No implementation necessary
  }

  writeValue(): void {
    // No implementation necessary
  }
}
